import React, { useState, useEffect, useRef, useMemo } from "react";
import CodeEditor from "./Editor";
import Preview from "./Preview";
import ConsolePanel from "./ConsolePanel";
import bgImg from '../assets/drawing.svg';
import { ChevronDown, Play } from "lucide-react";

const files = require.context(
    './defaultProjects/html', // relative to index.jsx
    false,                     // non-recursive
    /\.html$/                  // match .html
);

const HTMLFILES = {};
files.keys().forEach((key) => {
    HTMLFILES[key.replace('./', '')] = files(key).default || files(key);
});

console.log("Html Files are:", HTMLFILES);

const THEMES = [
  "vs",             // built-in light
  "vs-dark",        // built-in dark
  "hc-black",       // built-in high contrast
  "github",
  "dracula",
  "monokai",
  "solarized-dark",
  "solarized-light",
];

export default function Playground({importHtmlFile, html, css, js, setHtml, setCss, setJs, setIsLoading, lang, setLang, editorTheme, runKey, setGetPreviewUrlFn, isProjectsOpen, setIsProjectsOpen, setRunKey, setEditorTheme, getPreviewUrlFn }) {
  const [rightTab, setRightTab] = useState("preview");
  const [consoleEntries, setConsoleEntries] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);

  const editorRef = useRef(null);
  const themeMenuRef = useRef(null);
  const editorMenuRef = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      // basic shape check for messages coming from preview iframe
      if (!event.data || event.data?.source !== "preview") return;
      const { type, message } = event.data;
      if (!type || message === undefined) return;
      const t = type === "error" ? "error" : type === "warn" ? "warn" : type === "info" ? "info" : "log";
      setConsoleEntries((p) => [...p, { type: t, message: String(message), ts: Date.now() }]);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    (async () => {
      const results = await lintAll(html, css);
      setConsoleEntries((prev) => [...prev, ...results]);
    })();
  }, [runKey, html, css]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        openMenu &&
        !editorMenuRef.current?.contains(event.target) &&
        !themeMenuRef.current?.contains(event.target)
      ) {
        setOpenMenu(null);
      }
    }
    if (openMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu]);

  function quickHtmlChecks(html) {
    const entries = [];
    // Unclosed quotes in attributes
    const attrRegex = /\w+="[^"]*$/m;
    if (attrRegex.test(html)) {
      entries.push({
        type: "error",
        message: "HTML attribute value missing closing quote",
        ts: Date.now(),
        source: "html",
      });
    }
    return entries;
  }

  async function lintAll(html, css) {
    const newEntries = [];

    // Quick HTML checks
    const quickEntries = quickHtmlChecks(html);
    newEntries.push(...quickEntries);

    // HTMLHint
    try {
      const htmlModule = await import("htmlhint");
      const HTMLHint = (htmlModule).HTMLHint ?? htmlModule;
      const rules = {
        "tag-pair": true,
        "attr-lowercase": true,
        "id-unique": true,
        "attr-value-double-quotes": true,
      };
      const htmlProblems = HTMLHint.verify(html, rules);
      for (const p of htmlProblems) {
        newEntries.push({
          type: "error",
          message: `HTML: ${p.message}`,
          ts: Date.now(),
          source: "html",
          line: p.line,
          col: p.col,
          rule: p.rule?.id,
        });
      }
    } catch (e) {
      newEntries.push({
        type: "warn",
        message: `HTML linter failed: ${String(e?.message ?? e)}`,
        ts: Date.now(),
        source: "html",
      });
    }

    // CSSLint (unchanged)
    try {
      const cssModule = await import("csslint");
      const CSSLint = (cssModule).CSSLint ?? cssModule;
      const cssResult = CSSLint.verify(css, {
        "known-properties": true,
        "order-alphabetical": false,
        "box-model": false,
        "import": false,
      });
      const seen = new Set();
      for (const m of cssResult.messages || []) {
        const key = `${m.line}:${m.col}:${m.message}`;
        if (seen.has(key)) continue;
        seen.add(key);

        newEntries.push({
          type: m.type === "error" ? "error" : "warn",
          message: `CSS: ${m.message}`,
          ts: Date.now(),
          source: "css",
          line: m.line,
          col: m.col,
          rule: m.rule?.id ?? m.rule?.name,
        });
      }
    } catch (e) {
      newEntries.push({
        type: "warn",
        message: `CSS linter failed: ${String(e?.message ?? e)}`,
        ts: Date.now(),
        source: "css",
      });
    }

    return newEntries;
  }
  
  const clearConsole = () => setConsoleEntries([]);

  const jumpToEntry = (entry) => {
    if (!entry.line) return;
    // set editor language if necessary
    if (entry.source === "html") setLang("html");
    else if (entry.source === "css") setLang("css");
    else if (entry.source === "js") setLang("js");

    const ed = editorRef.current;
    if (!ed) return;
    // Monaco expects {lineNumber, column}
    const line = entry.line ?? 1;
    const col = entry.col ?? 1;

    // set position & reveal
    try {
      ed.setPosition({ lineNumber: line, column: col });
      ed.revealPositionInCenter({ lineNumber: line, column: col });
      ed.focus();
    } catch (err) {
      // older versions or different shape — ignore
      console.warn("couldn't jump to editor position", err);
    }
  };

  const defaultProjects = Object.keys(HTMLFILES).map((path) => {
    const name = path
      .split('/')
      .pop()
      .replace('.html', '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return { name, file: path };
  });

  const currentCode = lang === "html" ? html : lang === "css" ? css : js;
  const setCurrentCode = (v) => {
    if (lang === "html") setHtml(v);
    else if (lang === "css") setCss(v);
    else setJs(v);
  };

  const run = async () => {
    setRunKey((k) => k + 1);
  };

  const toggleMenu = (menu) => {
    setOpenMenu((prev) => (prev === menu ? null : menu));
  };

  const themeMenu = useMemo(() => (
    <div>
      {THEMES.map((theme) => (
        <button
          key={theme}
          className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100 capitalize"
          onMouseDown={() => {
            setEditorTheme(theme);
            setOpenMenu(null);
          }}
        >
          {theme.replace("-", " ")}
        </button>
      ))}
    </div>
  ), []);

  return (
    <div
      className="flex-1 flex gap-3 overflow-hidden"
      style={{
        backgroundImage: `url(${bgImg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Sidebar (persistent) */}
      <div
        className={`transition-all duration-300 h-full flex flex-row shadow-sm rounded-r-sm ${
          isProjectsOpen ? "w-80 opacity-100" : "w-0 opacity-0"
        } overflow-hidden`}
      >
        <div
          className={`h-full w-full bg-white flex flex-col transition-transform duration-300 ${
            isProjectsOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between bg-indigo-300 px-4 py-3 border-b">
            <h2 className="text-lg font-semibold text-center w-full">Default Projects</h2>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {defaultProjects.map((proj) => (
              <button
                key={proj.name}
                className="w-full text-left px-4 py-3 rounded-md my-1 cursor-pointer hover:bg-gray-100 transition-transform duration-300 hover:-translate-y-1 hover:translate-x-1 border-b-1 border-gray-100"
                onClick={async () => {
                  const text = HTMLFILES[proj.file]; // get file content directly
                  const { html, css, js } = await importHtmlFile(
                    new File([text], proj.file, { type: "text/html" }),
                    setIsLoading
                  );
                  setHtml(html);
                  setCss(css);
                  setJs(js);
                  setIsProjectsOpen(false);
                }}
              >
                {proj.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Editor */}
      <div className="flex-1 rounded-lg overflow-hidden bg-indigo-300 border border-gray-200 shadow-sm flex flex-col my-3">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {lang === "html" ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height='24px' width='24px'><path fill="#ff841f" d="M128 96L162.9 491.8L319.5 544L477.1 491.8L512 96L128 96zM436.2 223.9L252.4 223.9L256.5 273.3L432.1 273.3L418.5 421.7L320.6 448.7L320.6 449L319.5 449L220.8 421.7L214.8 345.9L262.5 345.9L266 384L319.5 398.5L373.2 384L379.2 321.8L212.3 321.8L199.5 176.2L440.6 176.2L436.2 223.9z"/></svg>
            ): lang === "css" ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height='24px' width='24px'><path fill="#351fffff" d="M128 96L162.9 491.8L320 544L477.1 491.8L512 96L128 96zM441.1 176L436.3 223.3L321 272.6L320.7 272.7L432.2 272.7L419.4 419.3L321.2 448L222.4 418.8L216 344.9L264.9 344.9L268.1 383.2L320.7 396.5L375.4 381.1L379.1 319.5L212.8 319L212.8 318.9L212.6 319L209 272.7L321.1 226L327.6 223.3L204.7 223.3L198.9 176L441.1 176z"/></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height='24px' width='24px'><rect x="96" y="96" width="448" height="448" fill="#ffdd1fff"/><path fill="black" d="M339.8 445.4C339.8 489 314.2 508.9 276.9 508.9C243.2 508.9 223.7 491.5 213.7 470.4L248 449.7C254.6 461.4 260.6 471.3 275.1 471.3C288.9 471.3 297.7 465.9 297.7 444.8L297.7 301.7L339.8 301.7L339.8 445.4zM439.4 508.9C400.3 508.9 375 490.3 362.7 465.9L397 446.1C406 460.8 417.8 471.7 438.5 471.7C455.9 471.7 467.1 463 467.1 450.9C467.1 436.5 455.7 431.4 436.4 422.9L425.9 418.4C395.5 405.5 375.4 389.2 375.4 354.9C375.4 323.3 399.5 299.3 437 299.3C463.8 299.3 483 308.6 496.8 333L464 354C456.8 341.1 449 336 436.9 336C424.6 336 416.8 343.8 416.8 354C416.8 366.6 424.6 371.7 442.7 379.6L453.2 384.1C489 399.4 509.1 415.1 509.1 450.3C509.1 488.1 479.3 508.9 439.4 508.9z"/></svg>
            )}
            <div className="text-sm font-medium uppercase">
              {lang}
            </div>
          </div>
          <div className="text-xs text-gray-500 flex flex-row gap-1">
            <div className="flex flex-row gap-1">
              <p className="text-xs text-white my-auto">Editor:</p>
              <div className="relative" ref={editorMenuRef}>
                <button
                  onClick={() => toggleMenu("editor")}
                  className="px-3 py-1.5 bg-white rounded-lg flex items-center cursor-pointer gap-1 hover:bg-grey-200"
                >
                  {lang} <ChevronDown size={16} />
                </button>
                <div className={`absolute top-full left-0 mt-2 w-40 bg-white shadow-lg rounded-lg border z-50 transition-all duration-300
                ${openMenu === "editor"? "max-h-[20rem] opacity-100 overflow-hidden" : "max-h-0 opacity-0 z-[-10]"}`}>
                  <button 
                    className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={() => {
                      setLang("html");
                      setOpenMenu(null);
                    }}
                  >
                    HTML
                  </button>
                  <button 
                    className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={() => {
                      setLang("css");
                      setOpenMenu(null);
                    }}
                  >
                    CSS
                  </button>
                  <button 
                    className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                    onMouseDown={() => {
                      setLang("js");
                      setOpenMenu(null);
                    }}
                  >
                    JavaScript
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <p className="text-xs text-white my-auto">Theme:</p>
              <div className="relative" ref={themeMenuRef}>
                <button
                  onClick={() => {toggleMenu("theme")}}
                  className="px-3 py-1.5 rounded-lg border bg-white flex items-center gap-1 cursor-pointer hover:bg-grey-700"
                >
                  {editorTheme} <ChevronDown size={16} />
                </button>
                <div className={`absolute top-full left-0 mt-2 w-40 bg-white shadow-lg rounded-lg border z-50 transition-all duration-300
                  ${openMenu === "theme"? "max-h-[20rem] opacity-100 overflow-hidden" : "max-h-0 opacity-0 z-[-10]"}`}>
                {themeMenu}
                </div>
              </div>
            </div>
            <button
              onClick={run}
              className="inline-flex items-center gap-2 px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 cursor-pointer text-white text-sm shadow"
              title="Run code"
            >
              <Play size={14} />
              <span>Run</span>
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <CodeEditor
            language={lang === "js" ? "javascript" : lang}
            code={currentCode ?? ""}
            onChange={setCurrentCode}
            theme={editorTheme}
            onEditorMount={(ed) => editorRef && (editorRef.current = ed)}
          />
        </div>
      </div>

      {/* Right column: preview / console */}
      <div className="w-96 flex flex-col gap-2 my-3 mr-3">
        <div className="rounded-lg overflow-hidden bg-indigo-300 border border-gray-200 shadow-sm flex flex-col h-full">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <div>
              <button
                onClick={() => setRightTab("preview")}
                className={`px-3 py-1 rounded text-sm cursor-pointer ${rightTab === "preview" ? "bg-gray-100 text-gray-900" : "text-gray-500"}`}
              >
                Preview
              </button>
              <button
                onClick={() => setRightTab("console")}
                className={`px-3 py-1 rounded text-sm cursor-pointer ${rightTab === "console" ? "bg-gray-100 text-gray-900" : "text-gray-500"}`}
              >
                Console{" "}
                {consoleEntries.length > 0 && (
                  <span className="ml-2 text-xs bg-red-500 text-white rounded-full px-2">{consoleEntries.length}</span>
                )}
              </button>
            </div>
            <div className="text-xs text-gray-500">
              <button
                className="px-2 py-1 border cursor-pointer rounded bg-white"
                title="Show Preview in new tab"
                onClick={() => {
                  if (!getPreviewUrlFn) return;

                  // generate a fresh Blob URL
                  const url = getPreviewUrlFn();
                  if (!url) return;

                  const win = window.open(url, "_blank");
                  if (!win) return; // just in case popups are blocked

                  // revoke after a short delay so tab can load
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                }}
              >
                Open in new tab
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 relative">
            <div
              className={`absolute inset-0 ${rightTab === "preview" ? "block" : "hidden"}`}
            >
              <Preview key={runKey} html={html} css={css} js={js} runKey={runKey} getPreviewUrl={setGetPreviewUrlFn} />
            </div>

            <div
              className={`absolute inset-0 ${rightTab === "console" ? "block" : "hidden"}`}
            >
              <ConsolePanel entries={consoleEntries} onClear={clearConsole} onEntryClick={jumpToEntry} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}